import { Box, Tab, Tabs } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getAllFunctionalities, getAllStructureAdmin } from 'services/API/Administration';
import { sendEvent } from 'utils/Event';
import { useTranslation } from 'react-i18next';
import { displayErrorMessage } from 'utils/tools_functions';
import DataGridUserRightPanel from './DataGridUserRightPanel';
import BasicObject from 'components/generics/Interface/Api/Common/BasicObject';
import IStructureList from 'components/generics/Interface/Api/Response/Structures/IStructureList';
import DataGridStructureRolePanel from './DataGridStructureRolePanel';

export const AccesRightManager = () => {
  const [functionalities, setFunctionalities] = useState<BasicObject[]>([]);
  const [structures, setStructures] = useState<BasicObject[]>([]);
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  useEffect(() => {
    fetchAllData();
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  const fetchAllData = async () => {
    try {
      const functionalitiesResponse = await fetchFunctionalities();
      const structuresResponse = await fetchStructures();

      setFunctionalities(functionalitiesResponse?.data.data);
      setStructures(
        structuresResponse?.data.data
          .map((structure: IStructureList) => ({
            id: structure.id,
            name: structure.name,
          }))
          .sort((a: BasicObject, b: BasicObject) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
          ),
      );
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const fetchFunctionalities = async () => {
    try {
      return await getAllFunctionalities();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(displayErrorMessage('ERR_GET_FUNCTIONALITIES'));
      }
    }
  };

  const fetchStructures = async () => {
    try {
      return await getAllStructureAdmin();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(displayErrorMessage('ERR_GET_COOP'));
      }
    }
  };

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange}>
          <Tab label={t('tab.rightsByUser')} {...a11yProps(0)} />
          <Tab label={t('tab.rightsByStructure')} {...a11yProps(1)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <DataGridUserRightPanel functionalities={functionalities} structures={structures} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <DataGridStructureRolePanel functionalities={functionalities} structures={structures} />
      </CustomTabPanel>
    </Box>
  );
};

export default AccesRightManager;

export type RightPanelParams = {
  functionalities: BasicObject[];
  structures: BasicObject[];
};
